import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import {
  House, Receipt, CreditCard, CheckCircle, Gear, SignOut, List, Bell, BellRinging, Question, Eye, Scroll
} from "@phosphor-icons/react";
import { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ScrollArea } from "../components/ui/scroll-area";
import api from "../lib/api";
import { toast } from "sonner";
import { isAdmin, canApprove, isAccountant, userRolesLabel } from "../lib/roles";

const navItems = [
  { to: "/", icon: House, label: "Dashboard", end: true },
  { to: "/transactions", icon: CreditCard, label: "Card Transactions" },
  { to: "/expenses", icon: Receipt, label: "Expenses" },
];

function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllRead, requestNotificationPermission, permissionState, pushSubscribed, ensurePushSubscription } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Auto-attempt subscription when opening the bell (silently — no prompts).
  // The visible status row below tells the user where they stand and gives
  // them a single button to fix whichever state they're in.
  useEffect(() => {
    if (!open) return;
    if (permissionState === "granted" && !pushSubscribed) {
      ensurePushSubscription();
    }
  }, [open, permissionState, pushSubscribed, ensurePushSubscription]);

  const sendTestPush = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test push sent — should appear on every device you're subscribed on.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test push failed — try Enable / Subscribe first.");
    }
  };

  const handleClick = (notif) => {
    markAsRead(notif.id);
    setOpen(false);
    if (notif.url) navigate(notif.url);
  };

  const typeIcon = (type) => {
    switch (type) {
      case "card_swipe": return <CreditCard size={14} weight="duotone" className="text-blue-500" />;
      case "expense_submitted": return <Receipt size={14} weight="duotone" className="text-amber-500" />;
      case "expense_approved": return <CheckCircle size={14} weight="duotone" className="text-emerald-500" />;
      case "expense_rejected": return <Receipt size={14} weight="duotone" className="text-red-500" />;
      default: return <Bell size={14} weight="duotone" className="text-zinc-400" />;
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-1.5 hover:bg-zinc-100 rounded-sm transition-colors" data-testid="notification-bell">
          {unreadCount > 0 ? (
            <BellRinging size={20} weight="duotone" className="text-[#002FA7]" />
          ) : (
            <Bell size={20} weight="duotone" className="text-zinc-400" />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 leading-none" data-testid="notification-badge">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 rounded-sm" align="end" sideOffset={8}>
        <div className="p-3 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500">Notifications</h3>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-[10px] text-[#002FA7] font-semibold hover:underline" data-testid="mark-all-read">
              Mark all read
            </button>
          )}
        </div>
        {/* Always-visible push status row. Shows where this device stands —
            permission denied / permission default / subscribed — and gives
            the user one button to advance to the next state, plus a Test
            button to verify the round-trip is working. Solves the
            "I can't find the Enable button" problem by never hiding it. */}
        <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200" data-testid="push-status-row">
          {permissionState === "denied" ? (
            <div className="flex items-start gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500 mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider">Notifications blocked by browser</p>
                <p className="text-[10px] text-zinc-500 mt-0.5 leading-snug">Click the lock icon in the address bar → allow notifications.</p>
              </div>
            </div>
          ) : permissionState !== "granted" ? (
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-zinc-600 flex-1 leading-snug">
                <span className="font-bold text-zinc-800">Pop-up notifications are off.</span><br />
                Enable to get alerts when this tab is closed.
              </p>
              <button onClick={requestNotificationPermission} data-testid="enable-push-btn"
                className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wider bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 rounded-sm transition-colors">
                Enable
              </button>
            </div>
          ) : !pushSubscribed ? (
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-zinc-600 flex-1 leading-snug">
                <span className="font-bold text-zinc-800">This device isn't subscribed.</span><br />
                Click Subscribe to register for push.
              </p>
              <button onClick={ensurePushSubscription} data-testid="subscribe-push-btn"
                className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wider bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 rounded-sm transition-colors">
                Subscribe
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-zinc-600 flex-1">
                <span className="font-bold text-zinc-800">Push notifications: ON</span> · this device is subscribed
              </p>
              <button onClick={sendTestPush} data-testid="test-push-btn"
                className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wider bg-zinc-200 hover:bg-zinc-300 text-zinc-700 px-2.5 py-1 rounded-sm transition-colors">
                Test
              </button>
            </div>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length > 0 ? (
            <div className="divide-y divide-zinc-100">
              {notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  data-testid={`notif-${n.id}`}
                  className={`w-full text-left px-3 py-2.5 flex gap-2.5 items-start hover:bg-zinc-50 transition-colors ${!n.read ? "bg-blue-50/50" : ""}`}
                >
                  <div className="mt-0.5 flex-shrink-0">{typeIcon(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs leading-tight ${!n.read ? "font-semibold text-zinc-900" : "text-zinc-600"}`}>
                      {n.title}
                    </p>
                    <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{n.body}</p>
                  </div>
                  <span className="text-[10px] text-zinc-300 flex-shrink-0 mt-0.5">{timeAgo(n.created_at)}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center">
              <Bell size={24} weight="duotone" className="text-zinc-200 mx-auto mb-1" />
              <p className="text-xs text-zinc-400">No notifications</p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout() {
  const { user, logout, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleExitPreview = async () => {
    await exitImpersonation();
    navigate("/settings");
  };

  const isImpersonating = Boolean(user?.impersonator_id);

  // Approvals shows for approver + administrator + accountant (read-only view);
  // Settings + Logs only for administrator.
  const accountant = isAccountant(user);
  const approverItems = (canApprove(user) || accountant) ? [{ to: "/approvals", icon: CheckCircle, label: "Approvals" }] : [];
  const settingsItems = isAdmin(user) ? [
    { to: "/settings", icon: Gear, label: "Settings" },
    { to: "/logs", icon: Scroll, label: "Logs" },
  ] : [];
  const allNavItems = [...navItems, ...approverItems, ...settingsItems];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="app-layout">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white px-4 py-2 flex items-center justify-between gap-3 shadow-md" data-testid="impersonation-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Eye size={16} weight="duotone" className="flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium truncate">
              Previewing as <span className="font-bold">{user?.name || user?.email}</span>
              <span className="hidden sm:inline ml-2 opacity-80">({userRolesLabel(user)})</span>
              {user?.impersonator_name && (
                <span className="hidden md:inline ml-2 opacity-70">— signed in as {user.impersonator_name}</span>
              )}
            </span>
          </div>
          <button
            onClick={handleExitPreview}
            data-testid="exit-preview-btn"
            className="flex items-center gap-1.5 px-3 py-1 bg-white text-amber-700 hover:bg-amber-50 rounded-sm text-xs font-bold flex-shrink-0 transition-colors"
          >
            <SignOut size={12} weight="bold" /> Exit Preview
          </button>
        </div>
      )}
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-56 border-r border-zinc-200 bg-white fixed h-full z-40">
        <div className="p-5 border-b border-zinc-200 flex items-center gap-2.5">
          <img src="/logo.svg" alt="Expenser logo" className="w-8 h-8 flex-shrink-0" data-testid="sidebar-logo" />
          <div className="min-w-0">
            <h1 className="text-lg font-black tracking-tighter text-zinc-950 leading-none" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Expenser
            </h1>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mt-1 whitespace-nowrap">Expense Management</p>
          </div>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {allNavItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-[#002FA7] text-white"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              {label}
            </NavLink>
          ))}
        </nav>
        {/* Notification bell in sidebar footer */}
        <div className="px-4 py-2 border-t border-zinc-100">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Alerts</span>
            <NotificationBell />
          </div>
        </div>
        <div className="p-3 border-t border-zinc-200">
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="w-7 h-7 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-xs font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-zinc-800 truncate">{user?.name}</p>
              <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{userRolesLabel(user)}</p>
            </div>
          </div>
          <a
            href="mailto:daniel.wayne@c3-contracting.com?subject=Expenser%20Support%20Request"
            data-testid="support-link"
            className="flex items-center gap-2 px-3 py-2 mt-1 w-full rounded-sm text-sm text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 transition-all duration-200"
          >
            <Question size={16} weight="duotone" />
            Get help
          </a>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 px-3 py-2 mt-1 w-full rounded-sm text-sm text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 transition-all duration-200"
          >
            <SignOut size={16} weight="duotone" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white/95 backdrop-blur border-b border-zinc-200 z-50 flex items-center justify-between px-4">
        <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn" className="p-1">
          <List size={22} weight="bold" className="text-zinc-700" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo.svg" alt="Expenser" className="w-6 h-6" />
          <h1 className="text-base font-black tracking-tighter text-zinc-950" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Expenser
          </h1>
        </div>
        <NotificationBell />
      </div>

      {/* Mobile Nav Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/30" onClick={() => setMobileOpen(false)}>
          <div className="w-64 h-full bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-zinc-200 flex items-center gap-2.5">
              <img src="/logo.svg" alt="Expenser" className="w-8 h-8 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-lg font-black tracking-tighter text-zinc-950 leading-none" style={{ fontFamily: 'DM Sans, sans-serif' }}>Expenser</h1>
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mt-1 whitespace-nowrap">Expense Management</p>
              </div>
            </div>
            <nav className="py-3 px-2 space-y-0.5">
              {allNavItems.map(({ to, icon: Icon, label, end }) => (
                <NavLink key={to} to={to} end={end} onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-sm font-medium ${isActive ? "bg-[#002FA7] text-white" : "text-zinc-600"}`
                  }>
                  <Icon size={18} weight="duotone" /> {label}
                </NavLink>
              ))}
            </nav>
            <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-zinc-200">
              <a href="mailto:daniel.wayne@c3-contracting.com?subject=Expenser%20Support%20Request" className="flex items-center gap-2 px-3 py-2.5 w-full text-sm text-zinc-500">
                <Question size={16} weight="duotone" /> Get help
              </a>
              <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2.5 w-full text-sm text-zinc-500">
                <SignOut size={16} weight="duotone" /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 lg:ml-56 pt-14 lg:pt-0 min-h-screen ${isImpersonating ? "mt-10" : ""}`}>
        <Outlet />
      </main>
    </div>
  );
}
